import { useState, useCallback, useEffect } from "react";
import { DocumentService } from "@/services/documentService";
import { DocumentResponse } from "@/types/api";
import { toast } from "sonner";

export const useDocuments = () => {
  const [documents, setDocuments] = useState<DocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Carregar documentos
  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const docs = await DocumentService.getAllDocuments();
      // logger.info('📄 Documentos carregados:', docs); // DEBUG
      setDocuments(docs);
    } catch (error) {
      console.error("Erro ao carregar documentos:", error);
      toast.error("Erro ao carregar documentos");
    } finally {
      setLoading(false);
    }
  }, []);

  // Upload de documento
  const uploadDocument = useCallback(
    async (file: File, description?: string) => {
      try {
        setUploading(true);
        // logger.info('📁 Enviando arquivo:', { name: file.name, size: file.size }); // DEBUG
        const uploadedDoc = await DocumentService.uploadDocument(
          file,
          description
        );
        // logger.info('✅ Documento enviado:', uploadedDoc); // DEBUG
        setDocuments((prev) => [uploadedDoc, ...prev]);
        toast.success(`${file.name} enviado com sucesso!`);
        return uploadedDoc;
      } catch (error) {
        console.error(`Erro ao enviar ${file.name}:`, error);
        toast.error(`Erro ao enviar ${file.name}`);
        throw error;
      } finally {
        setUploading(false);
      }
    },
    []
  );

  // Upload múltiplo
  const uploadMultipleDocuments = useCallback(
    async (files: FileList) => {
      if (!files.length) return;

      setUploading(true);
      const results = [];

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];

          // Validação básica
          if (file.size > 10 * 1024 * 1024) {
            // 10MB
            toast.error(`Arquivo ${file.name} é muito grande (máximo 10MB)`);
            continue;
          }

          try {
            const result = await uploadDocument(file);
            results.push(result);
          } catch (error) {
            // Erro já tratado no uploadDocument
          }
        }
      } finally {
        setUploading(false);
      }

      return results;
    },
    [uploadDocument]
  );

  // Deletar documento
  const deleteDocument = useCallback(async (id: string) => {
    try {
      await DocumentService.deleteDocument(id);
      setDocuments((prev) => prev.filter((doc) => doc.id !== id));
      toast.success("Documento removido");
    } catch (error) {
      console.error("Erro ao deletar documento:", error);
      toast.error("Erro ao remover documento");
      throw error;
    }
  }, []);

  // Download de documento
  const downloadDocument = useCallback(async (document: DocumentResponse) => {
    try {
      await DocumentService.downloadDocumentWithName(document);
      toast.success(`Download de ${document.originalName} iniciado`);
    } catch (error) {
      console.error("Erro ao fazer download:", error);
      toast.error("Erro ao fazer download do arquivo");
      throw error;
    }
  }, []);

  // Buscar documento por ID
  const getDocumentById = useCallback(async (id: string) => {
    try {
      return await DocumentService.getDocumentById(id);
    } catch (error) {
      console.error("Erro ao buscar documento:", error);
      toast.error("Erro ao buscar documento");
      throw error;
    }
  }, []);

  // Carregar documentos na inicialização
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  return {
    documents,
    loading,
    uploading,
    loadDocuments,
    uploadDocument,
    uploadMultipleDocuments,
    deleteDocument,
    downloadDocument,
    getDocumentById,
  };
};
